/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) 2021 Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import { observer } from 'mobx-react';
import React from 'react';
import { Button, Dialog, Grid, IconButton, Typography } from '@material-ui/core';
import { ModuleDetailViewUseStyles } from './ModuleDetailViewStyles';
import { FormattedMessage } from 'react-intl';
import CancelIcon from '@material-ui/icons/Cancel';
import { useViewModel } from '../../../../../hooks/useViewModel';
import { ModuleDetailViewModel } from './ModuleDetailViewModel';
import { ModuleDetailDataView } from './DataView/ModuleDetailDataView';
import { Image } from '../../../../../types/elements/Image';

interface Props {
	open: boolean;
	moduleName: string;
	moduleDescription: string;
	moduleImage?: Image;
	closeDialog: () => void;
	removeFromCourse: () => void;
	hasCourseStarted: boolean;
	canCourseBeEdited: boolean;
}

export const ModuleDetailView = observer((props: Props) => {
	const classes = ModuleDetailViewUseStyles();
	const viewModel = useViewModel(ModuleDetailViewModel);
	const { open, moduleName, moduleDescription, moduleImage, closeDialog, removeFromCourse, canCourseBeEdited } = props;

	return (
		<>
			<Dialog open={open} classes={{ paper: classes.paper }} fullWidth={true} onClose={closeDialog}>
				<Grid container className={classes.header}>
					<Grid item xs={4} className={classes.titleContainer}>
						<Typography variant='h1' className={classes.title}>
							<FormattedMessage id={'modules.module_detail.preview'} />
						</Typography>
					</Grid>
					<Grid item xs={8} className={classes.headerButtons}>
						{canCourseBeEdited && (
							<Button
								variant='outlined'
								size='small'
								className={classes.removeButton}
								classes={{ label: classes.buttonLabel }}
								onClick={removeFromCourse}>
								<FormattedMessage id={'modules.module_editing.remove_module'} />
							</Button>
						)}
						<IconButton className={classes.cancelButton} onClick={() => closeDialog()}>
							<CancelIcon />
						</IconButton>
					</Grid>
				</Grid>
				<Grid container className={classes.contentContainer}>
					<Grid item xs={6} className={classes.contentBox}>
						<ModuleDetailDataView
							moduleImage={moduleImage}
							moduleName={moduleName}
							moduleDescription={moduleDescription}
							setShowDetailImage={() => viewModel.setShowDetailImage(true)}
						/>
					</Grid>
				</Grid>
			</Dialog>
			{moduleImage && (
				<Dialog open={viewModel.getShowDetailImage()} onClose={() => viewModel.setShowDetailImage(false)} maxWidth='lg'>
					<img src={moduleImage.src} alt={moduleImage.alt} className={classes.detailImage} />
				</Dialog>
			)}
		</>
	);
});
